import { convertMeasurement, parseMeasurementUnit } from './measurement'

interface ProductCenterOfGravityInput {
  length: number | null
  width: number | null
  height: number | null
  dimensionUnit: string
  /** Coordonnées depuis le coin inférieur, toujours en mm. */
  x?: number | null
  y?: number | null
  z?: number | null
}

export function resolveProductCenterOfGravity(input: ProductCenterOfGravityInput) {
  const dimensions = [input.length, input.width, input.height].map(Number)
  const coordinates = [input.x, input.y, input.z]
  const automatic = coordinates.every(value => value == null)
  if (!dimensions.every(value => Number.isFinite(value) && value > 0)) {
    return { status: 'missing-dimensions' as const, pointMm: null, automatic }
  }
  const unit = parseMeasurementUnit(input.dimensionUnit)
  const sizesMm = dimensions.map(value => convertMeasurement(value, unit, 'mm'))
  const point = coordinates.map((value, index) => value == null ? sizesMm[index] / 2 : Number(value))
  if (point.some((value, index) => !Number.isFinite(value) || value < 0 || value > sizesMm[index])) {
    return { status: 'invalid-coordinates' as const, pointMm: null, automatic }
  }
  const geometric = point.every((value, index) => Math.abs(value - sizesMm[index] / 2) <= sizesMm[index] * 1e-9)
  return { status: 'ready' as const, pointMm: { x: point[0], y: point[1], z: point[2] }, automatic, geometric }
}

interface ProductCenterOfGravityFields {
  length?: number | null
  width?: number | null
  height?: number | null
  dimension_unit?: string | null
  center_of_gravity_x?: number | null
  center_of_gravity_y?: number | null
  center_of_gravity_z?: number | null
}

/** Complete only empty coordinates once real dimensions are available. Explicit zero is preserved. */
export function fillMissingProductCenterOfGravity<T extends ProductCenterOfGravityFields>(product: T): T {
  if (product.center_of_gravity_x != null && product.center_of_gravity_y != null && product.center_of_gravity_z != null) return product
  const center = resolveProductCenterOfGravity({
    length: product.length ?? null, width: product.width ?? null, height: product.height ?? null,
    dimensionUnit: product.dimension_unit || 'mm',
  }).pointMm
  if (!center) return product
  return {
    ...product,
    center_of_gravity_x: product.center_of_gravity_x ?? center.x,
    center_of_gravity_y: product.center_of_gravity_y ?? center.y,
    center_of_gravity_z: product.center_of_gravity_z ?? center.z,
  }
}
